<template>
  <div>
    <page-loading v-if="loading" message="Carregando produtos" />
    <template v-else>
      <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="product-tabs">
        <v-tab>Produtos</v-tab>
        <v-tab>Categorias</v-tab>
        <v-tab>Estoque</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" class="mt-4">
        <v-tab-item>
          <page-toolbar class="mb-4">
            <template #filters>
              <v-text-field
                v-model="search"
                label="Buscar produto"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                hide-details
                clearable
                class="product-search br-form-field"
                @keyup.enter="loadProducts"
                @click:clear="loadProducts"
              />
            </template>
            <template #actions>
              <v-btn dark color="primary" depressed @click="$router.push('/produtos/new')">
                <v-icon left>mdi-plus</v-icon>
                Novo produto
              </v-btn>
            </template>
          </page-toolbar>
          <data-table-card
            :headers="productHeaders"
            :items="products"
            :items-per-page="10"
            show-count
            no-data-text="Nenhum produto cadastrado"
          >
            <template v-slot:[`item.imageUrl`]="{ item }">
              <div class="br-product-thumb" :class="{ 'br-product-thumb--empty': !item.imageUrl }">
                <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.name" />
                <v-icon v-else size="20" color="primary">mdi-package-variant-closed</v-icon>
              </div>
            </template>
            <template v-slot:[`item.stockQuantity`]="{ item }">
              <span :class="{ 'product-stock--low': isLowStock(item) }">
                {{ item.stockQuantity }} {{ item.unitMeasure }}
              </span>
              <v-chip
                v-if="isLowStock(item)"
                x-small
                class="br-status-chip br-status-chip--warning ml-1"
              >
                Baixo
              </v-chip>
            </template>
            <template v-slot:[`item.salePrice`]="{ item }">
              <span class="product-price">{{ formatPrice(item.salePrice) }}</span>
            </template>
            <template v-slot:[`item.isActive`]="{ item }">
              <v-chip
                small
                class="br-status-chip"
                :class="item.isActive ? 'br-status-chip--active' : 'br-status-chip--inactive'"
              >
                {{ item.isActive ? "Ativo" : "Inativo" }}
              </v-chip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn icon class="br-icon-action" title="Movimentar estoque" @click="openMovementDialog(item)">
                <v-icon>mdi-package-variant-closed</v-icon>
              </v-btn>
              <v-btn icon class="br-icon-action" title="Editar produto" @click="$router.push(`/produtos/${item.id}`)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
          </data-table-card>
        </v-tab-item>

        <v-tab-item>
          <div class="categories-actions mb-4">
            <v-btn dark color="primary" depressed @click="openCategoryDialog()">
              <v-icon left>mdi-plus</v-icon>
              Nova categoria
            </v-btn>
          </div>
          <data-table-card
            :headers="categoryHeaders"
            :items="categories"
            :items-per-page="10"
            show-count
            no-data-text="Nenhuma categoria cadastrada"
          >
            <template v-slot:[`item.isActive`]="{ item }">
              <v-chip
                small
                class="br-status-chip"
                :class="item.isActive ? 'br-status-chip--active' : 'br-status-chip--inactive'"
              >
                {{ item.isActive ? "Ativa" : "Inativa" }}
              </v-chip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn icon class="br-icon-action" @click="openCategoryDialog(item)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
          </data-table-card>
        </v-tab-item>

        <v-tab-item>
          <form-section
            title="Registrar movimentação"
            description="Entradas e saídas de estoque para seus produtos."
            icon="mdi-package-variant-closed"
            class="mb-6"
          >
            <v-form ref="movementForm" v-model="movementFormValid">
              <v-row>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="movementForm.productId"
                      :items="products"
                      item-text="name"
                      item-value="id"
                      label="Produto *"
                      outlined
                      dense
                      :rules="[v => !!v || 'Obrigatório']"
                    >
                      <template v-slot:item="{ item }">
                        {{ item.name }} ({{ item.stockQuantity }} {{ item.unitMeasure }})
                      </template>
                      <template v-slot:selection="{ item }">
                        {{ item.name }}
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="movementForm.movementType"
                      :items="movementTypeOptions"
                      item-text="label"
                      item-value="value"
                      label="Tipo *"
                      outlined
                      dense
                      :rules="[v => !!v || 'Obrigatório']"
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model.number="movementForm.quantity"
                      label="Quantidade *"
                      type="number"
                      min="1"
                      outlined
                      dense
                      :rules="[
                        v => !!v || 'Obrigatório',
                        v => Number(v) > 0 || 'Mínimo 1',
                      ]"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="movementForm.notes"
                      label="Observação"
                      outlined
                      dense
                      placeholder="Ex: compra fornecedor, perda, doação"
                    />
                  </v-col>
                </v-row>
                <v-btn
                  dark
                  color="primary"
                  depressed
                  :loading="savingMovement"
                  :disabled="!movementFormValid"
                  @click="saveMovement"
                >
                  Registrar
                </v-btn>
            </v-form>
          </form-section>

          <v-row class="mb-2 align-center">
            <v-col cols="12" md="6">
              <v-select
                v-model="movementFilterProductId"
                :items="movementFilterOptions"
                item-text="name"
                item-value="id"
                label="Filtrar por produto"
                outlined
                dense
                clearable
                @change="loadMovements"
              />
            </v-col>
          </v-row>

          <v-data-table
            :headers="movementHeaders"
            :items="movements"
            :loading="loadingMovements"
            :items-per-page="10"
            no-data-text="Nenhuma movimentação registrada"
          >
            <template v-slot:[`item.createdAt`]="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
            <template v-slot:[`item.product`]="{ item }">
              {{ item.product?.name || "—" }}
            </template>
            <template v-slot:[`item.movementType`]="{ item }">
              <v-chip
                small
                class="br-status-chip"
                :class="movementTypeChipClass(item.movementType)"
              >
                {{ movementTypeLabel(item.movementType) }}
              </v-chip>
            </template>
            <template v-slot:[`item.quantity`]="{ item }">
              {{ item.quantity }}
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>

      <v-dialog v-model="categoryDialog" max-width="500">
        <v-card>
          <v-card-title>
            {{ editingCategory?.id ? "Editar categoria" : "Nova categoria" }}
          </v-card-title>
          <v-card-text>
            <v-text-field v-model="categoryForm.name" label="Nome" outlined dense />
            <v-textarea
              v-model="categoryForm.description"
              label="Descrição"
              outlined
              dense
              rows="2"
            />
            <v-switch v-model="categoryForm.isActive" label="Ativa" />
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="categoryDialog = false">Cancelar</v-btn>
            <v-btn dark color="primary" :loading="savingCategory" @click="saveCategory">
              Salvar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="movementDialog" max-width="500">
        <v-card>
          <v-card-title>Movimentar estoque</v-card-title>
          <v-card-subtitle v-if="movementProduct">
            {{ movementProduct.name }} — saldo atual: {{ movementProduct.stockQuantity }}
            {{ movementProduct.unitMeasure }}
          </v-card-subtitle>
          <v-card-text>
            <v-form ref="quickMovementForm" v-model="quickMovementFormValid">
              <v-select
                v-model="movementForm.movementType"
                :items="movementTypeOptions"
                item-text="label"
                item-value="value"
                label="Tipo *"
                outlined
                dense
                :rules="[v => !!v || 'Obrigatório']"
              />
              <v-text-field
                v-model.number="movementForm.quantity"
                label="Quantidade *"
                type="number"
                min="1"
                outlined
                dense
                class="mt-2"
                :rules="[
                  v => !!v || 'Obrigatório',
                  v => Number(v) > 0 || 'Mínimo 1',
                ]"
              />
              <v-text-field
                v-model="movementForm.notes"
                label="Observação"
                outlined
                dense
                class="mt-2"
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="movementDialog = false">Cancelar</v-btn>
            <v-btn
              dark
              color="primary"
              :loading="savingMovement"
              :disabled="!quickMovementFormValid"
              @click="saveMovement"
            >
              Registrar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import PageToolbar from "@/components/ui/PageToolbar.vue";
import DataTableCard from "@/components/ui/DataTableCard.vue";
import FormSection from "@/components/ui/FormSection.vue";
import { pdvApi } from "@/services/pdv/pdvApi";
import {
  CatalogProductType,
  InventoryMovementKind,
  InventoryMovementType,
  ProductCategoryType,
} from "@/types/pdv/Pdv";

export default defineComponent({
  components: { DataTableCard, FormSection, PageLoading, PageToolbar },
  data() {
    return {
      loading: true,
      activeTab: 0,
      search: "",
      products: [] as CatalogProductType[],
      categories: [] as ProductCategoryType[],
      movements: [] as InventoryMovementType[],
      loadingMovements: false,
      movementFilterProductId: "",
      categoryDialog: false,
      movementDialog: false,
      savingCategory: false,
      savingMovement: false,
      movementFormValid: false,
      quickMovementFormValid: false,
      movementProduct: null as CatalogProductType | null,
      editingCategory: null as ProductCategoryType | null,
      categoryForm: { name: "", description: "", isActive: true },
      movementForm: {
        productId: "",
        movementType: "ENTRY" as InventoryMovementKind,
        quantity: 1,
        notes: "",
      },
      movementTypeOptions: [
        { value: "ENTRY", label: "Entrada" },
        { value: "OUTPUT", label: "Saída" },
      ],
      productHeaders: [
        { text: "", value: "imageUrl", sortable: false, width: 56 },
        { text: "Nome", value: "name" },
        { text: "SKU", value: "sku" },
        { text: "Estoque", value: "stockQuantity" },
        { text: "Preço", value: "salePrice" },
        { text: "Status", value: "isActive" },
        { text: "", value: "actions", sortable: false },
      ],
      categoryHeaders: [
        { text: "Nome", value: "name" },
        { text: "Descrição", value: "description" },
        { text: "Status", value: "isActive" },
        { text: "", value: "actions", sortable: false },
      ],
      movementHeaders: [
        { text: "Data", value: "createdAt" },
        { text: "Produto", value: "product" },
        { text: "Tipo", value: "movementType" },
        { text: "Qtd", value: "quantity" },
        { text: "Saldo anterior", value: "previousBalance" },
        { text: "Saldo atual", value: "currentBalance" },
        { text: "Observação", value: "notes" },
      ],
    };
  },
  computed: {
    movementFilterOptions(): Array<{ id: string; name: string }> {
      return this.products.map((p) => ({ id: p.id, name: p.name }));
    },
  },
  watch: {
    "$route.query.tab"(tab) {
      if (tab === "categories") this.activeTab = 1;
      else if (tab === "estoque") this.activeTab = 2;
    },
    activeTab(tab) {
      if (tab === 1 && !this.categories.length) {
        this.loadCategories();
      }
      if (tab === 2) {
        this.loadMovements();
      }
    },
  },
  async mounted() {
    if (this.$route.query.tab === "categories") {
      this.activeTab = 1;
    } else if (this.$route.query.tab === "estoque") {
      this.activeTab = 2;
    }
    await Promise.all([this.loadProducts(), this.loadCategories()]);
    this.loading = false;
    if (this.activeTab === 2) {
      await this.loadMovements();
    }
  },
  methods: {
    async loadProducts() {
      const result = await pdvApi.listProducts({
        search: this.search || undefined,
        limit: 100,
      });
      this.products = result.data || [];
    },
    async loadCategories() {
      const result = await pdvApi.listCategories({ limit: 100 });
      this.categories = result.data || [];
    },
    async loadMovements() {
      this.loadingMovements = true;
      try {
        const result = await pdvApi.listInventoryMovements({
          productId: this.movementFilterProductId || undefined,
          limit: 50,
        });
        this.movements = result.data || [];
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao carregar movimentações",
          color: "error",
          timer: 3000,
        });
      } finally {
        this.loadingMovements = false;
      }
    },
    isLowStock(item: CatalogProductType) {
      return Number(item.minimumStock) > 0 && Number(item.stockQuantity) <= Number(item.minimumStock);
    },
    formatPrice(v: number) {
      return Number(v).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatDate(value: string) {
      return new Date(value).toLocaleString("pt-BR");
    },
    movementTypeLabel(type: InventoryMovementKind) {
      const labels: Record<InventoryMovementKind, string> = {
        ENTRY: "Entrada",
        OUTPUT: "Saída",
        ADJUSTMENT: "Ajuste",
      };
      return labels[type] || type;
    },
    movementTypeChipClass(type: InventoryMovementKind) {
      const classes: Record<InventoryMovementKind, string> = {
        ENTRY: "br-status-chip--entry",
        OUTPUT: "br-status-chip--output",
        ADJUSTMENT: "br-status-chip--adjustment",
      };
      return classes[type] || "br-status-chip--inactive";
    },
    openCategoryDialog(item?: ProductCategoryType) {
      this.editingCategory = item || null;
      this.categoryForm = item
        ? {
            name: item.name,
            description: item.description || "",
            isActive: item.isActive,
          }
        : { name: "", description: "", isActive: true };
      this.categoryDialog = true;
    },
    openMovementDialog(item: CatalogProductType) {
      this.movementProduct = item;
      this.movementForm = {
        productId: item.id,
        movementType: "ENTRY",
        quantity: 1,
        notes: "",
      };
      this.movementDialog = true;
    },
    resetMovementForm() {
      this.movementForm = {
        productId: "",
        movementType: "ENTRY",
        quantity: 1,
        notes: "",
      };
      this.movementProduct = null;
    },
    async saveMovement() {
      const productId = this.movementForm.productId || this.movementProduct?.id;
      if (!productId) return;

      this.savingMovement = true;
      try {
        await pdvApi.createInventoryMovement({
          productId,
          movementType: this.movementForm.movementType,
          quantity: Number(this.movementForm.quantity),
          notes: this.movementForm.notes || undefined,
        });
        this.movementDialog = false;
        this.resetMovementForm();
        await Promise.all([this.loadProducts(), this.loadMovements()]);
        this.$store.dispatch("showSnack", {
          message: "Movimentação registrada",
          color: "success",
          timer: 3000,
        });
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao registrar movimentação. Verifique se há estoque suficiente para saídas.",
          color: "error",
          timer: 4000,
        });
      } finally {
        this.savingMovement = false;
      }
    },
    async saveCategory() {
      this.savingCategory = true;
      try {
        if (this.editingCategory?.id) {
          await pdvApi.updateCategory(this.editingCategory.id, this.categoryForm);
        } else {
          await pdvApi.createCategory(this.categoryForm);
        }
        this.categoryDialog = false;
        await this.loadCategories();
        this.$store.dispatch("showSnack", {
          message: "Categoria salva",
          color: "success",
          timer: 3000,
        });
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao salvar categoria",
          color: "error",
          timer: 3000,
        });
      } finally {
        this.savingCategory = false;
      }
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

.product-tabs {
  background: $br-bg-elevated;
  border-radius: $br-radius-lg $br-radius-lg 0 0;
  border: 1px solid $br-border-soft;
  border-bottom: none;
  box-shadow: $br-shadow-sm;
  padding: 0 $br-space-2;
}

.product-price {
  font-weight: 800;
  color: $br-brand;
  font-variant-numeric: tabular-nums;
}

.product-stock--low {
  color: $br-brand-dark;
  font-weight: 700;
}

.categories-actions {
  display: flex;
  justify-content: flex-end;
}
</style>
